<template>
    <filters-wrapper :has-active-filters="hasActiveFilters" @submit="submit" @reset="reset">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.status" @update:model-value="submit" />
        </template>
        <v-form class="table-filters">
            <v-row class="pb-5" dense>
                <v-col cols="12" md="3">
                    <v-select
                        v-model="filters.dateType"
                        :items="campaignDataTypeItems"
                        :label="$t('date')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <daterange
                        :start-date-label="$t('date_from')"
                        :end-date-label="$t('date_to')"
                        dense
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.dateFrom"
                        :_end-date="filters.dateTo"
                        clearable
                        @change-start-date="filters.dateFrom = $event"
                        @change-end-date="filters.dateTo = $event"
                    />
                </v-col>
                <v-col cols="12" md="5" />
                <v-col cols="12" md="3">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="filters.campaignType"
                        :items="campaignTypeItems"
                        :label="$t('campaign_type')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                    />
                </v-col>
                <v-col cols="12" md="5" />
                <v-col cols="12" md="3">
                    <v-autocomplete
                        v-model="filters.assignedToId"
                        item-title="name"
                        item-value="personId"
                        :items="users"
                        :label="$t('assigned_to')"
                        :loading="usersLoading"
                        :disabled="usersLoading"
                        density="compact"
                        variant="outlined"
                        color="primary"
                    />
                    <v-checkbox
                        v-model="isChecked"
                        :label="$t('assigned_to_me')"
                        density="compact"
                        hide-details
                        class="float-left"
                        color="primary"
                        @update:model-value="$event ? (filters.assignedToId = personId) : (filters.assignedToId = null)"
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-text-field
                        v-model="filters.budgetFrom"
                        :label="$t('budget_from')"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-text-field
                        v-model="filters.budgetTo"
                        :label="$t('budget_to')"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                    />
                </v-col>
                <v-col>
                    <v-select
                        v-model="filters.budgetCurrency"
                        :items="currencyItems"
                        :label="$t('currency')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        class="width-max-content"
                        min-width="100"
                    />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import Daterange from '@/components/search/forms/Daterange.vue'
    import type {SelectOptionType} from '~/utils/types/types'

    @Component({
        components: {Daterange},
    })
    export class CampaignsFilters extends Vue {
        @VModel() filters
        @Prop({required: true, type: Boolean}) hasActiveFilters: boolean
        @Watch('filters.assignedToId')
        onLocaleChange(): void {
            this.isChecked = this.filters.assignedToId === this.personId
        }

        usersLoading: boolean = true
        isChecked: boolean = false
        users = []

        @Emit()
        submit() {}

        @Emit()
        reset() {}

        async mounted() {
            try {
                this.usersLoading = true
                const {users} = await this.$api.persons.get({companyId: authStore.company.id})
                const sortedUsers = users
                    .map(({personId, firstName, lastName}) => ({
                        personId,
                        name: `${lastName || ''} ${firstName || ''}`.trim(),
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name))
                this.users = sortedUsers
            } catch (e) {
                this.users = []
            } finally {
                this.usersLoading = false
            }
        }

        get campaignTypeItems(): SelectOptionType[] {
            const campaignTypes = ['EXHIBISION', 'ROAD_SHOW', 'ADVERTISEMENT', 'OTHER']
            return campaignTypes.map(value => ({value, title: this.$t(`campaign_types.${value}`)}))
        }

        get campaignDataTypeItems(): SelectOptionType[] {
            const campaignTypes = ['CREATION', 'MODIFIED', 'EXECUTION']
            return campaignTypes.map(value => ({value, title: this.$t(`date_types.${value}`)}))
        }

        get currencyItems(): SelectOptionType[] {
            const currencies = Object.values(this.$conf.public.currency.available).filter(currency =>
                Object.keys(runtimeStore.exchangeRates).includes(currency)
            )
            return currencies.map(value => ({value, title: value}))
        }

        get personId(): number {
            return authStore.person.personId
        }
    }

    export default CampaignsFilters
</script>
